import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import styled from "styled-components/macro";

import { useCardProvider } from "Payments/globalPaymentsStateHooks";
import { motion } from "framer-motion";

import useCreditCardEntryState from "./state";
import {
  InputOutline,
  SmallNumberInput,
  iPhone,
  iPhoneMini,
  useErrorShakeAnimation,
} from "./styles";

const ExpirationDate = forwardRef(
  ({ cardInputRef, cvcInputRef, submitting }, ref) => {
    const { providerIsWePay } = useCardProvider();
    const [isHovering, setIsHovering] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const [
      {
        month,
        year,
        ocr: { month: monthOCR, year: yearOCR },
        canBeEdited,
        isEditing,
        cardType,
      },
      dispatch,
    ] = useCreditCardEntryState();

    const cardTypeShortname = cardType?.shortname || "";

    const shouldShowDisplayOnly = canBeEdited && !isEditing;

    const setMonth = (month) => dispatch({ type: "Set Month", month });
    const setYear = (year) => dispatch({ type: "Set Year", year });

    const { shake: monthErrorShake, animationProps: monthErrorAnimationProps } =
      useErrorShakeAnimation();
    const { shake: yearErrorShake, animationProps: yearErrorAnimationProps } =
      useErrorShakeAnimation();
    const { shake: dateErrorShake, animationProps: errorAnimationProps } =
      useErrorShakeAnimation();

    const monthInputRef = useRef();
    const yearInputRef = useRef();

    useImperativeHandle(ref, () => ({
      focus: () => {
        monthInputRef.current.focus();
      },
      deleteFromCVC: () => {
        if (year.length === 2) {
          setYear(year.slice(0, -1));
        }
        setTimeout(() => yearInputRef.current.focus(), 1);
      },
      get monthInput() {
        return monthInputRef.current;
      },
      get yearInput() {
        return yearInputRef.current;
      },
    }));

    useEffect(() => {
      if (monthOCR.length > 0) updateMonth({ target: { value: monthOCR } });
      if (yearOCR.length > 0) updateYear({ target: { value: yearOCR } });
    }, [monthOCR, yearOCR]);

    const updateMonth = (e, onBlur = false) => {
      let newMonth;

      if (!onBlur)
        dispatch({
          type: "Remove Multiple Error Messages",
          messages: ["expiration_in_past", "invalid_expiration"],
        });

      if (e.target.value.length === 0) newMonth = "";
      else newMonth = e.target.value.replace(/\D/g, "");

      if (newMonth.length > 2) newMonth = newMonth.substr(-2);
      const newMonthNumber = newMonth * 1;

      if (
        e.nativeEvent &&
        e.nativeEvent.inputType !== "deleteContentBackward" &&
        e.nativeEvent.inputType !== "deleteContentForward"
      ) {
        if (newMonthNumber > 1 && newMonthNumber < 10) {
          newMonth = "0" + newMonthNumber;
        }
      }

      if (onBlur && newMonthNumber === 1) newMonth = "01";

      setMonth(newMonth);

      if (!onBlur) {
        if (newMonthNumber > 12) {
          monthErrorShake();
          monthInputRef.current.select();
          dispatch({
            type: "Set Error Message",
            message: "invalid_expiration",
          });
        } else {
          dispatch({
            type: "Remove Error Message",
            message: "invalid_expiration",
          });
          if (newMonth.length === 2) yearInputRef.current.focus();
        }
      }
    };

    const monthBlur = (e) => {
      updateMonth(e, true);
      setIsFocused(false);
      setTimeout(validateDate, 1);
    };

    const updateYear = (e, onBlur = false) => {
      let newYear = e.target.value.replace(/\D/g, "");

      if (newYear === "0" && month * 1 === 12) {
        newYear = "";
        setMonth("01");
      }

      if (newYear.length > 2) newYear = newYear.substr(-2);

      dispatch({
        type: "Remove Multiple Error Messages",
        messages: [
          "empty_expiration_date",
          "expiration_in_past",
          "invalid_expiration",
        ],
      });
      // validate when the user completes typing the year
      if (newYear.length === 2) {
        if (
          !onBlur &&
          new Date().getFullYear().toString().substr(-2) * 1 > newYear * 1
        ) {
          yearErrorShake();
          yearInputRef.current.select();

          dispatch({
            type: "Set Error Message",
            message: "expiration_in_past",
          });
        } else {
          validateDate(newYear, !onBlur);
        }
      }

      // validate when the user blurs the year field, but only has 1 digit in the field
      if (newYear.length === 1 && onBlur) {
        validateDate(newYear, !onBlur);
      }

      setYear(newYear);
    };

    const monthKeyDown = (e) => {
      if (e.code === "Backspace" && month.length === 0) {
        cardInputRef.current.focus();
      }
    };

    const yearKeyDown = (e) => {
      if (e.code === "Backspace" && year.length === 0) {
        setMonth(month.slice(0, -1));
        setTimeout(() => monthInputRef.current.focus(), 1);
      }
    };

    const yearBlur = (e) => {
      updateYear(e, true);
      setIsFocused(false);
    };

    const validateDate = (useYear = false, thenBlur = false) => {
      let yearToUse = useYear || year;
      if (
        month.length === 2 &&
        yearToUse.toString().length <= 2 &&
        (thenBlur ||
          (document.activeElement !== monthInputRef.current &&
            document.activeElement !== yearInputRef.current))
      ) {
        const currentYear = new Date().getFullYear().toString().substr(-2) * 1;

        if (parseInt(yearToUse) < currentYear) {
          dateErrorShake();
          dispatch({
            type: "Set Error Message",
            message: "expiration_in_past",
          });
          return;
        }
        if (parseInt(yearToUse) === currentYear) {
          const currentMonth = new Date().getMonth() + 1;
          if (month * 1 < currentMonth) {
            console.log("hit");
            dateErrorShake();
            dispatch({
              type: "Set Error Message",
              message: "expiration_in_past",
            });
            return;
          }
        }

        dispatch({
          type: "Remove Error Message",
          message: "expiration_in_past",
        });

        if (thenBlur) {
          setIsHovering(false);
          if (providerIsWePay) {
            cvcInputRef.current.focus();
          }
        }
      }
    };

    return (
      <Wrapper
        {...errorAnimationProps}
        onHoverStart={() => setIsHovering(true)}
        onHoverEnd={() => setIsHovering(false)}
      >
        <InputOutline
          isFocused={!submitting && isFocused}
          isHovering={!shouldShowDisplayOnly && !submitting && isHovering}
          placeholder="Expires"
        />

        <DateInputWrapper {...monthErrorAnimationProps}>
          <DateInput
            ref={monthInputRef}
            value={month}
            type="text"
            inputMode="numeric"
            name="ccmonth"
            autoComplete="cc-exp-month"
            tabIndex={providerIsWePay ? 2 : 0}
            onChange={updateMonth}
            onFocus={() => setIsFocused(true)}
            onKeyDown={monthKeyDown}
            onBlur={monthBlur}
            placeholder="MM"
            disabled={shouldShowDisplayOnly || submitting}
            useDarkPlaceholder={cardTypeShortname === "amex"}
            data-cy="fund-it-card-month-exp-input"
          />
        </DateInputWrapper>
        <Slash>{"/"}</Slash>
        <DateInputWrapper {...yearErrorAnimationProps}>
          <YearInput
            ref={yearInputRef}
            value={year}
            type="text"
            inputMode="numeric"
            name="ccyear"
            autoComplete="cc-exp-year"
            tabIndex={providerIsWePay ? 3 : 0}
            onChange={updateYear}
            onFocus={() => setIsFocused(true)}
            onBlur={yearBlur}
            onKeyDown={yearKeyDown}
            placeholder="YY"
            disabled={shouldShowDisplayOnly || submitting}
            useDarkPlaceholder={cardTypeShortname === "amex"}
            data-cy="fund-it-card-year-exp-input"
          />
        </DateInputWrapper>
      </Wrapper>
    );
  }
);

ExpirationDate.displayName = "ExpirationDate";
export default ExpirationDate;

const Wrapper = styled(motion.div)`
  position: relative;
  display: flex;
  align-items: center;
  max-width: fit-content;
  flex-grow: 0;
  padding: 10px 12px 7px 12px;
  @media screen and (max-width: ${iPhone.cssWidth}) {
    padding: ${iPhone.inputPadding};
  }
  @media screen and (max-width: ${iPhoneMini.cssWidth}) {
    padding: ${iPhoneMini.inputPadding};
  }
`;

const Slash = styled.div`
  font-size: 20px;
  font-family: Montserrat;
  line-height: 20px;
  margin: 0 2px;
  margin-top: 2px;
  text-shadow: rgba(0, 0, 0, 0.5) 1px 1px 2px;
  @media screen and (max-width: ${iPhone.cssWidth}) {
    font-size: 15px;
    margin-left: 0;
    margin-top: 0;
  }
`;

const DateInputWrapper = styled(motion.div)`
  position: relative;
  cursor: text;
`;

const DateInput = styled(SmallNumberInput)`
  max-width: 25px;
`;
const YearInput = styled(DateInput)`
  &::-webkit-input-placeholder,
  &::placeholder {
    transform: translate(1px, -2px);
  }
`;

const StaticInput = styled.div`
  font-size: 18px;
  height: 24px;
  margin-top: 1px;
`;
